import { useState, useEffect } from "react";
import { renderGoogleDocContent, extractLastUpdated } from "./useReadJsonDoc";

interface DocContent {
  html: string;
  lastUpdated?: string;
}

/**
 * Hook to read and process imported JSON file resources
 * @param jsonData - The imported JSON data
 * @param skipRender - Optional flag to skip HTML rendering (default: false)
 * @returns Processed document content with HTML and last updated date
 */
export function useReadJsonFile(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jsonData: any,
  skipRender: boolean = false,
): { content: DocContent; isLoading: boolean; error: Error | null } {
  const [content, setContent] = useState<DocContent>({ html: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Process the JSON in the next tick to avoid blocking the UI
    const processJson = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Validate the imported data
        if (!jsonData || !Array.isArray(jsonData)) {
          throw new Error("Invalid JSON data: Expected an array");
        }

        // Generate HTML from the JSON using the renderGoogleDocContent function
        const html = skipRender
          ? ""
          : renderGoogleDocContent(jsonData, {
              showDocumentTitle: false,
              hideFirstSectionTitle: true,
              hideFirstParagraphTitle: true,
            });

        // Extract last updated date using the imported function from useReadJsonDoc
        const lastUpdated = extractLastUpdated(jsonData) || "June 1, 2025"; // Add default fallback date

        setContent({ html, lastUpdated });
      } catch (err) {
        console.error("Error processing JSON data:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    processJson();
  }, [jsonData, skipRender]);

  return { content, isLoading, error };
}
